import {
    snakeCase,
    text,
    timestamp,
    varchar,
    integer,
    numeric,
    boolean,
    jsonb,
    index,
} from 'drizzle-orm/pg-core'
import { id, timestamps } from '../utils/columns.helpers'
import { targetingScopeEnum, benefitStatusEnum } from './_common.table'
import { benefitTypesTable } from './benefit-types.table'
import { serviceProviderCategoriesTable } from './service-provider-categories.table'
import { usersTable } from './users.table'
import { vendorsTable } from './vendors.table'

export interface CalendarExceptionDay {
    date: string
    status: 'available' | 'not-redeemable' | 'peak-season'
}

export const benefitsTable = snakeCase.table(
    'benefits',
    {
        id: id('bnf'),
        vendorId: text()
            .notNull()
            .references(() => vendorsTable.id, {
                onDelete: 'cascade',
                onUpdate: 'cascade',
            }),
        serviceProviderCategoryId: text()
            .notNull()
            .references(() => serviceProviderCategoriesTable.id, {
                onDelete: 'restrict',
                onUpdate: 'cascade',
            }),
        title: varchar({ length: 255 }).notNull(),
        shortDescription: varchar({ length: 255 }),
        description: text(),
        termsAndConditions: text(),
        benefitTypeId: text()
            .notNull()
            .references(() => benefitTypesTable.id, {
                onDelete: 'restrict',
                onUpdate: 'cascade',
            }),
        targetingScope: targetingScopeEnum().notNull(),

        // Dynamic Tier Performance Projections (Sub-5ms feed query)
        minStartingPriceCents: integer(),
        maxDiscountSummary: varchar({ length: 100 }),
        hasTiers: boolean().default(false),

        // Legacy single-tier fields (maintained for backward compatibility)
        flatAmountCents: integer(),
        percentageValue: numeric({
            precision: 5,
            scale: 2,
        }),
        minPurchaseCents: integer(),
        maxDiscountCents: integer(),
        maxCashbackCents: integer(),
        calendarExceptions: jsonb().$type<CalendarExceptionDay[]>().default([]),
        voucherCodePrefix: varchar({ length: 50 }),
        maxApplicationsPerEmployee: integer().notNull().default(1),
        maxTotalRedemptions: integer(),
        totalRedemptions: integer().default(0),
        status: benefitStatusEnum().notNull().default('draft'),
        validFrom: timestamp({ withTimezone: true }),
        validUntil: timestamp({ withTimezone: true }),
        creatorId: text().references(() => usersTable.id, {
            onDelete: 'set null',
            onUpdate: 'cascade',
        }),
        deletedAt: timestamp({ withTimezone: true }),
        ...timestamps,
    },
    (table) => [
        index('benefits_status_scope_idx').on(
            table.status,
            table.targetingScope,
            table.deletedAt,
        ),
        index('benefits_vendor_idx').on(table.vendorId),
    ],
)
